/**
 * UnmarkSignalModal — desfaz a sinalização de pagamento de uma fatura, com motivo.
 *
 * Quem vai perguntar "por que minha marcação sumiu?" é a própria cobrança, dias depois, olhando o
 * histórico da FATURA. Por isso o motivo é obrigatório aqui e não apenas no servidor: pedir depois
 * não funciona.
 *
 * ⚠️ A ação é do PAGAMENTO, não da fatura. As faturas de um pagamento formam uma conta que tem de
 * fechar; tirar uma do meio deixaria as irmãs marcadas numa conta que já não fecha. Quando é lote,
 * o aviso é explícito — o usuário clicou numa fatura e vai desmarcar várias.
 */
import { useEffect, useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { listInvoicesByPayment, unmarkInvoices } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

/** Texto do lote, combinado com quem opera a tela. */
/**
 * ⚠️ O texto mudou de sentido no AF-3671 step 4.
 *
 * Ele avisava que remover esta marcação levaria as IRMÃS junto — e levava mesmo, porque a conta do
 * pagamento tinha de fechar. Com a marcação parcial isso deixou de acontecer: as outras ficam onde
 * estão, e o pagamento passa a ter saldo a identificar. O aviso continua existindo porque saber que
 * a fatura está num lote ainda importa (o pagamento volta a aparecer como pendente), mas agora ele
 * informa em vez de ameaçar.
 */
export const BATCH_WARNING =
    'Esta fatura faz parte de um lote com outras que compõem o mesmo pagamento. '
    + 'Apenas esta perde a sinalização — as demais continuam marcadas, e o valor volta a ficar '
    + 'disponível para identificar.';

/**
 * O que a mensagem de sucesso diz.
 *
 * ⚠️ `cancelledCount` é sempre 0 desde o AF-3671 e o parâmetro fica por compatibilidade com a
 * resposta da API. A marcação deixou de criar fatura, então desmarcar não tem o que cancelar — o
 * ramo que anunciava o cancelamento continua aqui porque a API ainda pode voltar a informá-lo se
 * outro caminho cancelar algo, e anunciar de menos é pior que anunciar de mais.
 */
export const resultMessage = (unmarked: number, cancelledCount: number): string => {
    const base = unmarked > 1
        ? `Sinalização removida de ${unmarked} faturas.`
        : 'Sinalização removida.';
    if (!cancelledCount) return base;
    return `${base} ${cancelledCount === 1
        ? 'A fatura criada para este pagamento foi cancelada.'
        : `${cancelledCount} faturas criadas para este pagamento foram canceladas.`}`;
};

interface Props {
    invoice: any;
    /** id do pagamento sinalizado na fatura (`paid_by`) */
    paymentId: string;
    /**
     * Quantas faturas o pagamento tem marcadas, quando quem abre já sabe. OPCIONAL: a tela de
     * Títulos não sabe — ela lista as faturas de UM título, e o lote pode atravessar títulos que
     * ela nem carregou. Sem este valor o dialog conta sozinho.
     */
    linkedInvoicesCount?: number;
    onClose: () => void;
    onSuccess: () => void;
}

const UnmarkSignalModal: React.FC<Props> = ({
    invoice, paymentId, linkedInvoicesCount, onClose, onSuccess,
}) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    /**
     * Contagem descoberta aqui quando quem abriu não sabia.
     *
     * `null` = ainda não sei, e é DIFERENTE de 0: enquanto está nulo a tela diz que está
     * conferindo. Sem isso, o silêncio de meio segundo lê como "não é lote" — e o usuário que
     * digita o motivo rápido confirmaria sem nunca ver o aviso de que outras faturas cairão junto.
     */
    const [counted, setCounted] = useState<number | null>(null);

    useEffect(() => {
        if (linkedInvoicesCount != null) return;   // quem abriu já sabia
        let alive = true;
        listInvoicesByPayment(paymentId)
            .then(invoices => { if (alive) setCounted((invoices || []).length); })
            // falhar a contagem não pode travar a remoção: o servidor desmarca o lote de todo
            // jeito, e o aviso é ajuda, não pré-requisito
            .catch(() => { if (alive) setCounted(null); });
        return () => { alive = false; };
    }, [paymentId, linkedInvoicesCount]);

    const total = linkedInvoicesCount ?? counted;
    const isBatch = (total || 0) > 1;

    const confirm = async () => {
        if (!reason.trim()) return toast.warn('Informe o motivo da remoção');
        setSubmitting(true);
        try {
            /**
             * ⚠️ Remove SÓ a fatura que o usuário abriu.
             *
             * A chamada ia sem a lista, e o servidor entendia isso como "desfaça o pagamento
             * inteiro" — clicar em remover numa fatura levava as irmãs junto. Era o comportamento
             * correto enquanto a conta do pagamento tinha de fechar; com a marcação parcial, tirar
             * uma do meio deixa um saldo a identificar, que é um estado que a tela sabe mostrar.
             */
            const res = await unmarkInvoices(paymentId, reason.trim(), [invoice.invoiceId]);
            // a rota responde no envelope {data, meta}; o `??` cobre chamada direta em teste
            const unmarked = res?.data?.unmarked ?? res?.unmarked ?? [];
            const cancelledCount = unmarked.filter((u: any) => u.cancelled).length;
            toast.success(resultMessage(unmarked.length, cancelledCount));
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível remover a sinalização');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" /> Remover sinalização
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    Fatura {String(invoice.invoiceId).slice(0, 8)} · {formatBRL(invoice.amount)}
                    {' · '}o pagamento volta a ficar pendente de identificação.
                </p>

                {total == null && (
                    <p className="text-xs text-gray-500 mb-3">
                        Conferindo se há outras faturas neste pagamento...
                    </p>
                )}

                {isBatch && (
                    <div className="px-3 py-2 mb-3 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-300">
                        {BATCH_WARNING}.
                    </div>
                )}

                {/* o que acontece com a fatura DEPENDE de ela ter nascido para este pagamento, e a
                    tela não tem como saber qual é o caso — dizer os dois é honesto e evita a
                    surpresa de ver uma fatura cancelada */}
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                    Faturas que já existiam continuam abertas. As que foram criadas para este
                    pagamento são canceladas — sem ele, elas cobrariam o mesmo título duas vezes.
                </p>

                <label className="block text-sm font-medium mb-1" htmlFor="motivo-remocao">
                    Motivo *
                </label>
                <textarea
                    id="motivo-remocao"
                    className="w-full text-sm border rounded p-2 mb-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Título trocado, valor rateado errado, pagamento de outro cliente..."
                />
                <p className="text-xs text-gray-500 mb-3">
                    Fica no histórico da fatura — é o que responde depois "por que a marcação sumiu?".
                </p>

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={confirm} disabled={submitting || !reason.trim()}
                        className="px-4 py-2 text-sm rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60">
                        {submitting ? 'Removendo...' : 'Remover sinalização'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnmarkSignalModal;
